import { Platform } from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import * as Sharing from 'expo-sharing';
// Use the legacy file-system function API only. The new `expo-file-system`
// (SDK 54) exports `File`/`Directory` classes that extend a native base which
// is `undefined` on web, so importing it at module load crashes static web
// rendering ("Class extends value undefined" — expo/expo#39440). These two
// functions live in `/legacy`, which is web-safe.
import { cacheDirectory, readAsStringAsync, writeAsStringAsync } from 'expo-file-system/legacy';
import type { Trip, Place, Transport, CustomEntry, CardTag } from '@my-fullstack-app/itinerary-types';
import { migrateTripPlaces } from '@my-fullstack-app/itinerary-types';
import { migrateTripCosts } from './cost-migration';
import { generateId } from './id';
import { parsePlacePhotoRef } from './place-photo-url';

/** Strip API key from photoUrl and prefer photoRef in exports. */
function sanitizePlaceForExport(p: Place): Place {
  const { photoUrl, photoRef, ...rest } = p;
  const out: Place = { ...rest };
  if (photoRef) out.photoRef = photoRef;
  if (photoUrl && !photoUrl.includes("key=")) out.photoUrl = photoUrl;
  else if (photoRef && p.placeId) {
    out.photoUrl = `https://places.googleapis.com/v1/places/${p.placeId}/photos/${photoRef}/media?maxWidthPx=400`;
  }
  return out;
}

export const EXPORT_FILE_NAME = 'travel-itinerary.json';

function stripDuration<T extends { durationMinutes?: number | undefined}>(item: T): Omit<T, 'durationMinutes'> {
  const { durationMinutes: _durationMinutes, ...rest } = item;
  return rest;
}

function sanitizeTrips(trips: Trip[]): Trip[] {
  return trips.map((t) => ({
    ...t,
    days: t.days.map((d) => ({
      ...d,
      places: d.places.map((p) => stripDuration(p as Place & { durationMinutes?: number | undefined})),
      transports: (d.transports ?? []).map((tr) => stripDuration(tr as Transport & { durationMinutes?: number | undefined})),
      customs: (d.customs ?? []).map((c) => stripDuration(c as CustomEntry & { durationMinutes?: number | undefined})),
    })),
  }));
}

export interface ExportContext {
  /** Store's notesMap. Folded inline onto each entity to match the wire shape
   *  the sync layer ships (see injectInlineNotes in sync-manager). */
  notesMap?: Record<string, string> | undefined;
  /** Store's tagsMap. Scoped to this trip's entity ids and emitted as
   *  Trip.tags so the import side can rebuild tagsMap. */
  tagsMap?: Record<string, CardTag> | undefined;
}

function collectEntityIds(trip: Trip): Set<string> {
  const ids = new Set<string>([trip.id]);
  for (const d of trip.days) {
    ids.add(d.id);
    for (const p of d.places) ids.add(p.id);
    for (const t of d.transports ?? []) ids.add(t.id);
    for (const c of d.customs ?? []) ids.add(c.id);
  }
  for (const s of trip.stays ?? []) ids.add(s.id);
  for (const j of trip.journeys ?? []) ids.add(j.id);
  return ids;
}

export function serializeTrips(trips: Trip[], ctx?: ExportContext | undefined): string {
  const notesMap = ctx?.notesMap;
  const tagsMap = ctx?.tagsMap;
  const forExport = trips.map((t) => {
    const ids = collectEntityIds(t);
    const scopedTags = tagsMap
      ? Object.fromEntries(Object.entries(tagsMap).filter(([id]) => ids.has(id)))
      : undefined;
    return {
      ...t,
      notes: notesMap?.[t.id] ?? t.notes,
      tags: scopedTags && Object.keys(scopedTags).length > 0 ? scopedTags : t.tags,
      days: t.days.map((d) => ({
        ...d,
        notes: notesMap?.[d.id] ?? d.notes,
        places: d.places.map((p) => {
          const cleaned = sanitizePlaceForExport(stripDuration(p as Place & { durationMinutes?: number | undefined}));
          return notesMap ? { ...cleaned, notes: notesMap[p.id] ?? cleaned.notes } : cleaned;
        }),
        transports: (d.transports ?? []).map((tr) => {
          const stripped = stripDuration(tr as Transport & { durationMinutes?: number | undefined}) as Transport;
          return notesMap ? { ...stripped, notes: notesMap[stripped.id] ?? stripped.notes } : stripped;
        }),
        customs: (d.customs ?? []).map((c) => {
          const stripped = stripDuration(c as CustomEntry & { durationMinutes?: number | undefined}) as CustomEntry;
          return notesMap ? { ...stripped, notes: notesMap[stripped.id] ?? stripped.notes } : stripped;
        }),
      })),
      stays: t.stays
        ? t.stays.map((s) => (notesMap ? { ...s, notes: notesMap[s.id] ?? s.notes } : s))
        : t.stays,
      journeys: t.journeys
        ? t.journeys.map((j) => (notesMap ? { ...j, notes: notesMap[j.id] ?? j.notes } : j))
        : t.journeys,
    };
  });
  return JSON.stringify({ trips: forExport, version: 1 }, null, 2);
}

export function deserializeTrips(json: string): Trip[] {
  try {
    const parsed = JSON.parse(json);
    const raw = parsed?.trips && Array.isArray(parsed.trips) ? parsed.trips : Array.isArray(parsed) ? parsed : [];
    return sanitizeTrips(raw);
  } catch {
    return [];
  }
}

export async function exportTrips(
  trips: Trip[],
  singleTrip?: Trip | undefined,
  ctx?: ExportContext | undefined,
): Promise<void> {
  const data = singleTrip ? [singleTrip] : trips;
  const json = serializeTrips(data, ctx);

  if (Platform.OS === 'web') {
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = singleTrip
      ? `${singleTrip.name.replace(/\s+/g, '-')}-itinerary.json`
      : EXPORT_FILE_NAME;
    a.click();
    URL.revokeObjectURL(url);
  } else {
    const isAvailable = await Sharing.isAvailableAsync();
    if (isAvailable && cacheDirectory) {
      const filename = singleTrip
        ? `${singleTrip.name.replace(/\s+/g, '-')}-itinerary.json`
        : EXPORT_FILE_NAME;
      const fileUri = `${cacheDirectory}${filename}`;
      await writeAsStringAsync(fileUri, json);
      await Sharing.shareAsync(fileUri, {
        mimeType: 'application/json',
        dialogTitle: 'Export itinerary',
      });
    }
  }
}

export interface PreparedImport {
  trip: Trip;
  /** Entity-keyed notes ready to merge into the store's notesMap. */
  notes: Record<string, string>;
  /** Entity-keyed tags ready to merge into the store's tagsMap. */
  tags: Record<string, CardTag>;
}

/** Returns a copy of the trip with new IDs throughout (avoids conflicts when
 *  adding to store) plus the notes/tags maps re-keyed to those new IDs.
 *
 *  The wire schema (`Trip.notes` and inline `entity.notes` strings,
 *  `Trip.tags` Record) is split into the store's canonical shape:
 *  notesMap and tagsMap. The trip returned has its inline `notes`
 *  fields stripped — mirrors the post-hydrate invariant in sync-manager. */
export function prepareImportedTrip(trip: Trip): PreparedImport {
  // ADR-0055: migrate legacy stays/journeys to the unified PlaceRef
  // shape before id-rekeying — readers downstream all expect `place`.
  // ADR-0069: fold legacy single-cost / pricePaid / costAttribution
  // into per-line-item `costs[]`. Both are idempotent.
  const migrated = migrateTripCosts(migrateTripPlaces(trip));
  const idMap: Record<string, string> = {};
  const remap = (oldId: string): string => {
    const existing = idMap[oldId];
    if (existing) return existing;
    const fresh = generateId();
    idMap[oldId] = fresh;
    return fresh;
  };

  const newTripId = remap(migrated.id);
  const newDays = migrated.days.map((d) => {
    const newDayId = remap(d.id);
    const newPlaces = d.places.map((p) => {
      const newId = remap(p.id);
      const parsed = !p.photoRef && p.photoUrl && p.placeId
        ? parsePlacePhotoRef(p.photoUrl, p.placeId)
        : null;
      const upgraded: Place = {
        ...p,
        id: newId,
        notes: undefined,
        ...(parsed ? { photoRef: parsed.photoRef, photoUrl: undefined } : {}),
      };
      return upgraded;
    });
    const newTransports = (d.transports ?? []).map((t) => ({
      ...t,
      id: remap(t.id),
      fromPlaceId: t.fromPlaceId ? idMap[t.fromPlaceId] ?? t.fromPlaceId : undefined,
      toPlaceId: t.toPlaceId ? idMap[t.toPlaceId] ?? t.toPlaceId : undefined,
      notes: undefined,
    }));
    const newCustoms = (d.customs ?? []).map((c) => ({
      ...c,
      id: remap(c.id),
      notes: undefined,
    }));
    return {
      ...d,
      id: newDayId,
      places: newPlaces,
      transports: newTransports,
      customs: newCustoms,
      notes: undefined,
    };
  });
  const newStays = (migrated.stays ?? []).map((s) => ({
    ...s,
    id: remap(s.id),
    notes: undefined,
  }));
  const newJourneys = (migrated.journeys ?? []).map((j) => ({
    ...j,
    id: remap(j.id),
    notes: undefined,
  }));

  // ADR-0069: cost line items now live inline on each entity
  // (Place / CustomEntry / Booking on Journey + Stay), so they
  // ride along automatically when we clone the entity. No
  // separate remap needed — attribution `paidBy` / `splitWith` /
  // `categoryId` reference participants / categories which
  // aren't remapped on import either.

  const notes = extractImportedNotes(migrated, idMap);
  const tags = extractImportedTags(migrated, idMap);

  return {
    trip: {
      ...migrated,
      id: newTripId,
      days: newDays,
      stays: migrated.stays ? newStays : migrated.stays,
      journeys: migrated.journeys ? newJourneys : migrated.journeys,
      // heroPlaceId references a place.id that has just been re-issued. Without
      // remapping, the saved cover would silently revert to "no override" and
      // the UI would pick a stable fallback (the export's "header image
      // bookmark not respected" bug).
      heroPlaceId: migrated.heroPlaceId ? idMap[migrated.heroPlaceId] ?? undefined : undefined,
      notes: undefined,
      tags: undefined,
    },
    notes,
    tags,
  };
}

/** Walk the imported trip and collect notes by entity id, re-keyed via idMap.
 *
 *  Handles two shapes for `Trip.notes`:
 *    (a) JSON-stringified Record<entityId, string> — the canonical wire format
 *        per the Trip.notes doc comment; this is how exports from this app
 *        and server payloads typically encode it.
 *    (b) A plain string treated as the trip-level note (legacy / fallback). */
function extractImportedNotes(trip: Trip, idMap: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  const key = (oldId: string) => idMap[oldId] ?? oldId;

  if (trip.notes) {
    let consumedAsMap = false;
    try {
      const parsed = JSON.parse(trip.notes);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        for (const [oldId, content] of Object.entries(parsed as Record<string, unknown>)) {
          if (typeof content === 'string' && content.length > 0) {
            out[key(oldId)] = content;
          }
        }
        consumedAsMap = true;
      }
    } catch {
      // Fall through to plain-string handling.
    }
    if (!consumedAsMap) out[key(trip.id)] = trip.notes;
  }

  for (const day of trip.days) {
    if (day.notes) out[key(day.id)] = day.notes;
    for (const place of day.places) {
      if (place.notes) out[key(place.id)] = place.notes;
    }
    for (const transport of day.transports ?? []) {
      if (transport.notes) out[key(transport.id)] = transport.notes;
    }
    for (const custom of day.customs ?? []) {
      if (custom.notes) out[key(custom.id)] = custom.notes;
    }
  }
  for (const stay of trip.stays ?? []) {
    if (stay.notes) out[key(stay.id)] = stay.notes;
  }
  for (const journey of trip.journeys ?? []) {
    if (journey.notes) out[key(journey.id)] = journey.notes;
  }
  return out;
}

function extractImportedTags(trip: Trip, idMap: Record<string, string>): Record<string, CardTag> {
  const out: Record<string, CardTag> = {};
  if (!trip.tags) return out;
  for (const [oldId, tag] of Object.entries(trip.tags)) {
    out[idMap[oldId] ?? oldId] = tag;
  }
  return out;
}

export async function importTrips(): Promise<Trip[]> {
  if (Platform.OS === 'web') {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json,application/json';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          const text = await file.text();
          resolve(deserializeTrips(text));
        } else {
          resolve([]);
        }
      };
      input.click();
    });
  } else {
    const result = await DocumentPicker.getDocumentAsync({
      type: 'application/json',
      copyToCacheDirectory: true,
    });
    if (result.canceled || !result.assets.length) return [];
    const uri = result.assets[0]!.uri;
    const text = await readAsStringAsync(uri);
    return deserializeTrips(text);
  }
}
